import React, { useEffect, useState, useMemo, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SortingRule } from "react-table";
import TableComponent, {
  OperationsCell,
} from "../../components/TableComponent";
import Layout from "../../components/Layout/Layout";
import Routes from "../../helpers/Routes";
import Toast from "../../helpers/Toast";
import { Utilities } from "../../helpers/Utilities";
import { IHolidayDTO } from "../../models/Holiday/Iholidaydto";
import { Parameter } from "../../models/Parameter";
import { HolidayService } from "../../services/HolidayService";
import ButtonComponent from "../../components/ButtonComponent";
import {Col, Row, Modal, ModalHeader, ModalBody, ModalFooter,} from "reactstrap";


export default function HolidayIndex() {
  const intl = useIntl();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const service = new HolidayService();

  const pageSizes: number[] = [5, 10, 15, 30];

  const initialPage = Utilities.LoadPageFromURLQuery("page");
  const initialPageSize = Utilities.LoadPageSizeFromURLQuery(pageSizes, 10);
  const initialSortBy = Utilities.LoadSortParametersFromURLQuery(
    "sortField",
    "sortOrder"
  );
  const initialName = Utilities.LoadParameterFromURLQuery("name", "string", "");
  const initialDateStr = Utilities.LoadParameterFromURLQuery(
    "date",
    "date",
    ""
  );
  const initialStatus = Utilities.LoadParameterFromURLQuery(
    "status",
    "string",
    ""
  );

  const [loading, setLoading] = useState(false);
  const [holidays, setHolidays] = useState<IHolidayDTO[]>([]);
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(initialPage || 1);
  const [currentPageSize, setCurrentPageSize] = useState(initialPageSize);
  const [currentSorting, setCurrentSorting] = useState<
    SortingRule<object>[] | null
  >(initialSortBy);
  const [name, setName] = useState(initialName);
  const [status, setStatus] = useState(initialStatus);
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    if (!initialDateStr) return null;
    const parsedDate = new Date(initialDateStr);
    return !isNaN(parsedDate.getTime()) ? parsedDate : null;
  });

  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [sourceYear, setSourceYear] = useState<string>(
    new Date().getFullYear().toString()
  );
  const [targetYear, setTargetYear] = useState<string>(
    (new Date().getFullYear() + 1).toString()
  );
  const [sourceYearHolidays, setSourceYearHolidays] = useState<IHolidayDTO[]>([]);
  const [isLoadingSourceHolidays, setIsLoadingSourceHolidays] = useState(false);
  const [copyFeedbackMessage, setCopyFeedbackMessage] = useState("");
  const [copyOperationInProgress, setCopyOperationInProgress] = useState(false);

  const [goToPage, setGoToPage] = useState<number>(0);
  const [reloadSearch, setReloadSearch] = useState(false);

  const commonInputStyle = {
    width: "100%",
    height: "38px",
    fontSize: "1rem",
    lineHeight: "1.5",
    padding: "0.375rem 0.75rem",
    border: "1px solid #ced4da",
    borderRadius: "8px",
    boxSizing: "border-box" as "border-box"
  };

  const CustomOperationsCell = (id: number, type: string) => {
    return OperationsCell(id.toString(), type);
  };

  const activeStatusStyle = {
    color: "#07bc0c"
  };
  
  const inactiveStatusStyle = {
    color: "#e74c3c"
  };

  const getDateValue = (dateValue: any): number => {
    if (!dateValue) return Number.MIN_SAFE_INTEGER;
    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? Number.MIN_SAFE_INTEGER : dateValue.getTime();
    }
    if (typeof dateValue === "string" || typeof dateValue === "number") {
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? Number.MIN_SAFE_INTEGER : date.getTime();
    }
    return Number.MIN_SAFE_INTEGER;
  };

  const getStatusValue = (statusValue: any): boolean => {
    if (typeof statusValue === "boolean") return statusValue;
    if (typeof statusValue === "string") return statusValue === "true";
    return false;
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setStatus(value);
    setCurrentPage(1);
    setGoToPage(1);
    setReloadSearch(true);
  };

  const columns = useMemo(
    () => [
      {
        Header: "",
        accessor: "operations",
        Cell: (props: any) =>
          CustomOperationsCell(props.row.original?.id, "Holiday"),
        disableSortBy: true,
        width: 100,
      },
      {
        Header: intl.formatMessage({ id: "Screens.Holiday.Table.Column1" }),
        accessor: "name",
        width: 50,
        sortType: (rowA: any, rowB: any, columnId: string) => {
          const a = (rowA.values[columnId] || "").toString().toLowerCase();
          const b = (rowB.values[columnId] || "").toString().toLowerCase();
          if (a < b) return -1;
          if (a > b) return 1;
          return 0;
        },
      },
      {
        Header: intl.formatMessage({ id: "Screens.Holiday.Table.Column2" }),
        accessor: "date",
        width: 25,
        sortType: (rowA: any, rowB: any, columnId: string) => {
          const dateA = getDateValue(rowA.values[columnId]);
          const dateB = getDateValue(rowB.values[columnId]);
          return dateA - dateB;
        },
        filterFn: (row: any, columnId: string, filterValue: string) => {
          if (!filterValue) return true;
          const rowDate = row.getValue(columnId);
          if (!rowDate) return false;
          const d = new Date(rowDate);
          if (isNaN(d.getTime())) return false;
          const year = d.getFullYear();
          const month = (d.getMonth() + 1).toString().padStart(2, "0");
          const day = d.getDate().toString().padStart(2, "0");
          const rowDateStr = `${year}-${month}-${day}`;
          return rowDateStr === filterValue;
        },
        Cell: ({ value }: { value: any }) => (
          <>{formatDateString(value)}</>
        ),
      },
      {
        Header: intl.formatMessage({ id: "Estado" }),
        accessor: "active",
        width: 25,
        sortType: (rowA: any, rowB: any, columnId: string) => {
          const a = getStatusValue(rowA.values[columnId]);
          const b = getStatusValue(rowB.values[columnId]);
          return (b ? 1 : 0) - (a ? 1 : 0);
        },
        Cell: ({ value }: { value: boolean }) => (
          <span style={value ? activeStatusStyle : inactiveStatusStyle}>
            {value
              ? intl.formatMessage({ id: "Ativo" })
              : intl.formatMessage({ id: "Inativo" })}
          </span>
        ),
      },
    ],
    [intl]
  );

  const formatDateParam = (date: Date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return "";
    }
    return date.toISOString().split("T")[0];
  };
  const formatDateString = (value: string | number | Date): string => {
    const date = new Date(value);
    return isNaN(date.getTime()) ? "" : date.toLocaleDateString("pt");
  };
  const mountSearchParameters = useCallback((): Parameter[] => {
    const params: Parameter[] = [];
    if (name && name.trim() !== "") {
      params.push(new Parameter("name", name));
    }
    if (selectedDate && !isNaN(selectedDate.getTime())) {
      const year = selectedDate.getFullYear();
      const month = (selectedDate.getMonth() + 1).toString().padStart(2, "0");
      const day = selectedDate.getDate().toString().padStart(2, "0");
      params.push(new Parameter("date", `${year}-${month}-${day}`));
    }
    if (status === "true" || status === "false") {
      params.push(new Parameter("Active", status));
    }
    return params;
  }, [name, selectedDate, status]);

  const fetchData = useCallback(
    async (
      page: number,
      pageSize: number,
      sortBy: SortingRule<object>[] | null
    ) => {
      let searchUrl = `?page=${page}&pageSize=${pageSize}`;
      if (name && name.trim() !== "") {
        searchUrl += Utilities.AddParamToQueryIfValue("name", "string", name);
      }
      if (selectedDate && !isNaN(selectedDate.getTime())) {
        const year = selectedDate.getFullYear();
        const month = (selectedDate.getMonth() + 1).toString().padStart(2, "0");
        const day = selectedDate.getDate().toString().padStart(2, "0");
        const formattedDate = `${year}-${month}-${day}`;
        searchUrl += Utilities.AddParamToQueryIfValue(
          "date",
          "string",
          formattedDate
        );
      }
      if (status === "true" || status === "false") {
        searchUrl += Utilities.AddParamToQueryIfValue("Active", "string", status);
      }

      let sortParams: SortingRule<object>[] = [];
      if (sortBy && sortBy.length > 0) {
        sortParams = sortBy;
        searchUrl += Utilities.AddParamToQueryIfValue(
          "sortField",
          "string",
          sortBy[0].id
        );
        searchUrl += Utilities.AddParamToQueryIfValue(
          "sortOrder",
          "string",
          sortBy[0].desc ? "DESC" : "ASC"
        );
      } else if (currentSorting && currentSorting.length > 0) {
        sortParams = currentSorting;
        searchUrl += Utilities.AddParamToQueryIfValue(
          "sortField",
          "string",
          currentSorting[0].id
        );
        searchUrl += Utilities.AddParamToQueryIfValue(
          "sortOrder",
          "string",
          currentSorting[0].desc ? "DESC" : "ASC"
        );
      }

      if (searchParams.toString() === "") {
        navigate(`/Holidays${searchUrl}`, { replace: true });
      } else {
        navigate(`/Holidays${searchUrl}`);
      }
      setLoading(true);

      try {
        const response = await service.GetAll(
          mountSearchParameters(),
          Utilities.MountSortParameters(sortParams),
          page,
          pageSize
        );

        if (!response.success) {
          setHolidays([]);
          setPageCount(0);
          setCurrentPageSize(5);
          setCurrentPage(1);
          setCurrentSorting(null);
          Toast.Show("error", response.message);
          return;
        }

        setHolidays(response.items);
        setPageCount(response.totalPages);
        setCurrentPageSize(pageSize);
        setCurrentPage(page);
        setCurrentSorting(sortParams);
        setGoToPage(0);

        if (response.totalPages > 0 && page > response.totalPages) {
          setGoToPage(1);
        }
      } catch (error) {
        console.error("Error fetching holidays:", error);
        Toast.Show("error", "An unexpected error occurred");
        setHolidays([]);
        setPageCount(0);
      } finally {
        setLoading(false);
      }
    },
    [
      name,
      selectedDate,
      status,
      navigate,
      searchParams,
      mountSearchParameters,
      service,
      currentSorting 
    ]
  );

  useEffect(() => {
    fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
  }, []);

  useEffect(() => {
    if (reloadSearch) {
      fetchData(1, currentPageSize ?? 5, currentSorting);
      setReloadSearch(false);
    }
  }, [reloadSearch, fetchData, currentPageSize, currentSorting]);

  // Função para filtrar apenas ativos ou inativos ao selecionar no filtro
  useEffect(() => {
    // Quando o filtro de status muda, dispara busca para filtrar no backend
    if (status === "true" || status === "false") {
      setCurrentPage(1);
      setGoToPage(1);
      setReloadSearch(true);
    }
  }, [status]);

  const onRemoveFiltersClick = useCallback(() => {
    setCurrentPage(1);
    setGoToPage(1);
    setName("");
    setSelectedDate(null);
    setStatus("");
    setReloadSearch(true);
  }, []);

  const onEnter = useCallback(() => {
    fetchData(
      goToPage ?? currentPage ?? 1,
      currentPageSize ?? 10,
      currentSorting
    );
  }, [fetchData, goToPage, currentPage, currentPageSize, currentSorting]);

  const onButtonClick = useCallback(() => {
    setCurrentPage(1);
    setGoToPage(1);
    fetchData(1, currentPageSize ?? 10, currentSorting);
  }, [fetchData, currentPageSize, currentSorting]);

  const onNewTaskClick = useCallback(() => {
    navigate(Routes.Holidays.create);
  }, [navigate]);

  const openCopyModal = async () => {
    setCopyModalOpen(true);
    setCopyFeedbackMessage("");
    fetchSourceYearHolidays();
  };

  const closeCopyModal = () => {
    setCopyModalOpen(false);
    setCopyFeedbackMessage("");
    setSourceYearHolidays([]);
  };

  const fetchSourceYearHolidays = async () => {
    if (!sourceYear) return;
    
    setIsLoadingSourceHolidays(true);
    
    try {
      const startDate = new Date(parseInt(sourceYear), 0, 1);
      const endDate = new Date(parseInt(sourceYear), 11, 31);
      
      const response = await service.GetAll(
        [
          new Parameter("startDate", formatDateParam(startDate)),
          new Parameter("endDate", formatDateParam(endDate))
        ],
        [],
        1,
        1000
      );

      if (response.success) {
        const holidaysInYear = response.items.filter(holiday => {
          const holidayDate = new Date(holiday.date);
          return holidayDate.getFullYear() === parseInt(sourceYear);
        });
        
        setSourceYearHolidays(holidaysInYear);
      } else {
        Toast.Show("error", response.message);
        setSourceYearHolidays([]);
      }
    } catch (error) {
      console.error("Error fetching source year holidays:", error);
      Toast.Show("error", "An unexpected error occurred while retrieving holidays");
      setSourceYearHolidays([]);
    } finally {
      setIsLoadingSourceHolidays(false);
    }
  };

  useEffect(() => {
    if (copyModalOpen && sourceYear) {
      fetchSourceYearHolidays();
    }
  }, [sourceYear]);

  const copyHolidaysToTargetYear = async () => {
    if (sourceYearHolidays.length === 0 || !targetYear) {
      Toast.Show("error", intl.formatMessage({ id: "Screens.Holiday.Copy.NoHolidays" }));
      return;
    }

    if (sourceYear === targetYear) {
      Toast.Show("info", intl.formatMessage({ id: "Screens.Holiday.Copy.SameYear" }));
      return;
    }

    setCopyOperationInProgress(true);
    
    try {
      let successCount = 0;
      let errorCount = 0;
      
      for (const holiday of sourceYearHolidays) {
        const originalDate = new Date(holiday.date);
        const newDate = new Date(
          parseInt(targetYear),
          originalDate.getMonth(),
          originalDate.getDate()
        );
        
        const holidayToCopy = {
          ...holiday,
          id: 0, 
          date: newDate
        };
        
        try {
          const response = await service.Create(holidayToCopy);
          if (response.success) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          errorCount++;
          console.error(`Error copying holiday ${holiday.name}:`, error);
        }
      }
      
      if (successCount > 0) {
        const message = intl.formatMessage(
          { id: "Feriados copiados." },
          { 
            count: successCount, 
            sourceYear: sourceYear,
            targetYear: targetYear 
          }
        );
        
        Toast.Show("success", message);
        setCopyFeedbackMessage(message);
        
        fetchData(currentPage, currentPageSize, currentSorting);
      }
      
      if (errorCount > 0) {
        Toast.Show(
          "error",
          intl.formatMessage(
            { id: "Screens.Holiday.Copy.SomeErrors" },
            { count: errorCount }
          )
        );
      }
    } catch (error) {
      console.error("Error during batch copy operation:", error);
      Toast.Show(
        "error",
        intl.formatMessage({ id: "Screens.Holiday.Copy.Error" })
      );
    } finally {
      setCopyOperationInProgress(false);
    }
  };

  return (
    <Layout>
      <div className="pageContainer">
        <div className="pageHeader">
          <span className="pageTitle">
            <FormattedMessage id="Screens.Holiday.Table.Column1" />
          </span>
        </div>

        <Row>
          <Col
            xl={6}
            lg={8}
            md={8}
            sm={12}
            xs={12}
            style={{ marginTop: "1em", display: "flex", gap: "1rem" }}
          >
            <ButtonComponent
              text={intl.formatMessage({
                id: "Screens.Holiday.Button.CreateHoliday",
              })}
              onClick={onNewTaskClick}
            />
            <ButtonComponent
              text={intl.formatMessage({
                id: "Common.Buttons.Copy",
              })}
              onClick={openCopyModal}
              className="btn-outline-primary"
            />
          </Col>
        </Row>

        <div className="pageBody">
          <Col>
            <Row>
              <Col xl={4} lg={4} md={4} sm={12} xs={12}>
                <div className="form-group">
                  <label htmlFor="nameInput">
                    {intl.formatMessage({
                      id: "Screens.Holiday.Filters.Name",
                    })}
                  </label>
                  <input
                    id="nameInput"
                    type="text"
                    className="form-control"
                    style={commonInputStyle}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") onEnter();
                    }}
                  />
                </div>
              </Col>

              <Col xl={4} lg={4} md={4} sm={12} xs={12}>
                <div className="form-group">
                  <label htmlFor="dateInput">
                    {intl.formatMessage({
                      id: "Screens.Holiday.Filters.date",
                    })}
                  </label>
                  <input
                    id="dateInput"
                    type="date"
                    className="form-control"
                    style={commonInputStyle}
                    value={
                      selectedDate instanceof Date && !isNaN(selectedDate.getTime())
                        ? selectedDate.toISOString().split("T")[0]
                        : ""
                    }
                    onChange={(e) => {
                      if (e.target.value) {
                        const [year, month, day] = e.target.value.split("-");
                        const newDate = new Date(Number(year), Number(month) - 1, Number(day));
                        setSelectedDate(newDate);
                        setCurrentPage(1);
                        setGoToPage(1);
                        setReloadSearch(true);
                      } else {
                        setSelectedDate(null);
                        setCurrentPage(1);
                        setGoToPage(1);
                        setReloadSearch(true);
                      }
                    }}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") onEnter();
                    }}
                  />
                </div>
              </Col>

              <Col xl={4} lg={4} md={4} sm={12} xs={12}>
                <div className="form-group">
                  <label htmlFor="statusSelect">
                    {intl.formatMessage({
                      id: "Estado",
                    })}
                  </label>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <select
                      id="statusSelect"
                      className="form-control"
                      value={status}
                      onChange={handleStatusChange}
                      style={commonInputStyle}
                    >
                      <option value="true">
                        {intl.formatMessage({ id: "Ativo" })}
                      </option>
                      <option value="false">
                        {intl.formatMessage({ id: "Inativo" })}
                      </option>
                    </select>
                  </div>
                </div>
              </Col>
            </Row>
          </Col>

          <Row>
            <Col
              xl={3}
              lg={3}
              md={3}
              sm={12}
              xs={12}
              style={{ marginTop: "1em", display: "inline-flex", gap: "1rem" }}
            >
              <ButtonComponent
                text={intl.formatMessage({ id: "Common.Buttons.Search" })}
                onClick={onButtonClick}
              />
              <ButtonComponent
                text={intl.formatMessage({
                  id: "Common.Buttons.RemoveFilters",
                })}
                onClick={onRemoveFiltersClick}
              />
            </Col>
          </Row>

          <div className="pageComponent" style={{ width: "100%" }}>
            <TableComponent
              goToPage={goToPage}
              loading={loading}
              pageCount={pageCount}
              columns={columns}
              manualPagination={true}
              fetchData={fetchData}
              pageSizes={pageSizes}
              initialPage={initialPage}
              initialPageSize={initialPageSize}
              initialSortBy={initialSortBy}
              data={holidays}
              dontOrderFirstCollumn={true}
            />
          </div>
        </div>
      </div>
      
      <Modal
        isOpen={copyModalOpen}
        toggle={closeCopyModal}
        centered={true}
        className="modal-dialog-centered"
      >
        <ModalHeader
          toggle={closeCopyModal}
          className="text-dark"
        >
          <div className="d-flex align-items-center">
            {intl.formatMessage({ id: "Screens.Holiday.Copy.Title" })}
          </div>
        </ModalHeader>
        <ModalBody>
          {copyFeedbackMessage && (
            <div className="alert alert-success mb-3">
              {copyFeedbackMessage}
            </div>
          )}
          
          <div className="form-group mb-4">
            <label htmlFor="sourceYearInput" className="font-weight-bold">
              {intl.formatMessage({ id: "Screens.Holiday.Copy.SourceYear" })}
            </label>
            <div className="d-flex align-items-center">
              <input
                id="sourceYearInput"
                type="number"
                className="form-control"
                style={{ ...commonInputStyle, maxWidth: "200px" }}
                value={sourceYear}
                onChange={(e) => setSourceYear(e.target.value)}
                min={2000}
                max={2050}
              />
            </div>
          </div>
          
          {isLoadingSourceHolidays ? (
            <div className="d-flex justify-content-center my-4">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="mb-4">
              <h6 className="font-weight-bold mb-2">
                {intl.formatMessage({ id: "Screens.Holiday.Copy.SourceHolidays" })}:
              </h6>
              
              {sourceYearHolidays.length === 0 ? (
                <div className="alert alert-warning">
                  {intl.formatMessage({ id: "Screens.Holiday.Copy.NoHolidaysFound" })}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-striped">
                    <thead className="thead-light">
                      <tr>
                        <th>{intl.formatMessage({ id: "Screens.Holiday.Table.Column1" })}</th>
                        <th>{intl.formatMessage({ id: "Screens.Holiday.Table.Column2" })}</th>
                        <th>{intl.formatMessage({ id: "Screens.Holiday.Table.Status" })}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sourceYearHolidays.map((holiday) => (
                        <tr key={holiday.id}>
                          <td>{holiday.name}</td>
                          <td>{formatDateString(holiday.date)}</td>
                          <td>
                            <span style={
                              typeof holiday.active === 'string' 
                                ? holiday.active === "true" ? activeStatusStyle : inactiveStatusStyle
                                : holiday.active ? activeStatusStyle : inactiveStatusStyle
                            }>
                              {typeof holiday.active === 'string'
                                ? holiday.active === "true"
                                  ? intl.formatMessage({ id: "Common.Status.Active" })
                                  : intl.formatMessage({ id: "Common.Status.Inactive" })
                                : holiday.active
                                  ? intl.formatMessage({ id: "Common.Status.Active" })
                                  : intl.formatMessage({ id: "Common.Status.Inactive" })
                              }
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          
          <div className="form-group mt-4">
            <label htmlFor="targetYearInput" className="font-weight-bold">
              {intl.formatMessage({ id: "Screens.Holiday.Copy.TargetYear" })}
            </label>
            <input
              id="targetYearInput"
              type="number"
              className="form-control"
              style={{ ...commonInputStyle, maxWidth: "200px" }}
              value={targetYear}
              onChange={(e) => setTargetYear(e.target.value)}
              min={2000}
              max={2050}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonComponent
            text={intl.formatMessage({ id: "Common.Buttons.Cancel" })}
            onClick={closeCopyModal}
            className="btn-outline-secondary"
          />
          <ButtonComponent
            text={intl.formatMessage({ id: "Common.Buttons.Copy" })}
            onClick={copyHolidaysToTargetYear}
            disabled={sourceYearHolidays.length === 0 || !targetYear || copyOperationInProgress}
          />
        </ModalFooter>
      </Modal>
    </Layout>
  );
}